import type { FloatingSelectOption } from '../../../../../shared/components/floating-select/floating-select';

/**
 * Sentinel option value for a legacy “Please Select” placeholder.
 * Kept so validation can reject it if it ever appears in state — never submit it.
 */
export const PICKUP_LOCATION_PLEASE_SELECT_VALUE = '__PLEASE_SELECT__';

/** Salesforce pickupLocationCode for Front Door — default when no retained selection. */
export const DEFAULT_PICKUP_LOCATION_CODE = 'FD';

/**
 * Builds Pickup Location dropdown options from `GET /pickup/locations`, sorted
 * alphabetically by customer-facing label (case-insensitive). Option values are
 * the two-character Salesforce codes. No Please Select sentinel.
 *
 * @param pickupFromMap Code → customer-facing label
 */
export function buildPickupLocationOptions(
  pickupFromMap: Record<string, string>,
): FloatingSelectOption[] {
  return Object.entries(pickupFromMap)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/**
 * Resolves the Pickup Location code after locations load: keep a retained valid
 * selection, otherwise default to Front Door when present in the map.
 *
 * @param pickupFromMap Code → label map from the locations API
 * @param previousCode Previously selected or hydrated code, if any
 * @returns Code to bind, or null when Front Door is unavailable and previous is invalid
 */
export function resolveDefaultPickupLocationCode(
  pickupFromMap: Record<string, string>,
  previousCode: string | null,
): string | null {
  if (previousCode && Object.hasOwn(pickupFromMap, previousCode)) {
    return previousCode;
  }

  if (Object.hasOwn(pickupFromMap, DEFAULT_PICKUP_LOCATION_CODE)) {
    return DEFAULT_PICKUP_LOCATION_CODE;
  }

  return null;
}

/**
 * Returns true when `code` is a real Salesforce pickup location code present in the map.
 */
export function isSelectablePickupLocationCode(
  code: string | null | undefined,
  pickupFromMap: Record<string, string> | null,
): boolean {
  if (!code || code === PICKUP_LOCATION_PLEASE_SELECT_VALUE) {
    return false;
  }
  if (!pickupFromMap) {
    return false;
  }
  return Object.hasOwn(pickupFromMap, code);
}
